import { useCallback, useRef } from 'react';
import { useDispatch, useStore } from 'react-redux';
import * as Sentry from '@sentry/react';
import { useHistory } from 'react-router-dom';

import pusher from '../utils/pusher';
import { usePusherReconnect } from './usePusherReconnect';
import { getConversationsAction } from '../redux/conversationsDuck';
import { resyncSeatsAction } from '../redux/conversationLimitDuck';

/**
 * Tamaño de página con el que el chat recarga la bandeja tras una reconexión.
 * Es el MISMO que usa `ChatView` al montar (`getConversationsAction(true, 20, …)`),
 * a propósito: la recuperación debe dejar la pantalla igual que una recarga
 * manual, ni más corta ni más larga.
 */
export const CHAT_RECONNECT_PAGE_SIZE = 20;

const segmentsOf = (pathname) => {
  if (typeof pathname !== 'string') return [];
  const [withoutQuery] = pathname.split('?');
  const [withoutHash] = withoutQuery.split('#');
  return withoutHash.split('/').filter(Boolean);
};

/**
 * ¿La ruta actual es la del chat? Se compara el PRIMER segmento, no
 * `includes('/chat')`, para no confundirse con rutas que sólo contienen la
 * palabra (p.ej. `/settings/chat-widget`).
 */
export const isChatPath = (pathname) => segmentsOf(pathname)[0] === 'chat';

/**
 * Id de la conversación abierta, leído de la URL (`/chat/:chatId?`).
 *
 * ⚠️ **Es un respaldo, NO la fuente principal.** La ruta `/chat/:chatId?` existe
 * y `ChatView` la lee al montar, pero **la app nunca navega con el id**:
 * seleccionar una conversación es puro Redux
 * (`setConversationAsSelectedAction`), sin navegación. Todas las navegaciones
 * del árbol van a `/chat` a secas. Así que en la práctica esto devuelve `null`
 * salvo que alguien entre por un enlace directo.
 *
 * Fiarse de esto como fuente única fue un error real: hacía que la recuperación
 * cayera SIEMPRE en la rama sin-id, que es la destructiva (ver abajo).
 */
export const chatIdFromPath = (pathname) => {
  const segments = segmentsOf(pathname);
  if (segments[0] !== 'chat') return null;
  return segments[1] || null;
};

/**
 * Id de la conversación abierta según Redux. **Ésta sí es la fuente real.**
 */
export const openConversationId = (state) =>
  state?.conversations?.current?.id || null;

/**
 * ¿Hay ya una recarga de la bandeja en vuelo?
 *
 * Es el mismo `fetching` que `ChatView` mira para tapar el área de chat con el
 * loader, así que responder `true` aquí significa literalmente "ahora mismo el
 * asesor está viendo el spinner".
 */
export const isFetchingConversations = (state) =>
  state?.conversations?.fetching === true;

/**
 * Recuperación del tiempo real en el chat.
 *
 * El problema (DASHBOARD-ZC-KT, y el síntoma detrás de -KK y -KV): cuando la
 * conexión de Pusher se cae, pusher-js reconecta y vuelve a suscribirse solo,
 * pero **los eventos ocurridos durante la caída se pierden para siempre**. El
 * asesor se queda con una bandeja congelada y sólo la ve al recargar a mano —
 * que es justo la queja que llegó de SCPodólogos, VINTE y SieteTrece.
 *
 * El hook `usePusherReconnect` ya existía y ya resolvía exactamente esto…
 * pero estaba montado **sólo** en `views/Board/index.jsx`. El chat nunca lo
 * usó. Este hook es el equivalente para el chat: al reconectar, vuelve a
 * pedir la bandeja tal como lo hace `ChatView` al montar, conservando la
 * conversación abierta si la hay.
 *
 * Se monta en `DashboardLayout` (el layout por el que pasan todas las rutas
 * autenticadas, incluida `/chat`) y no dentro de `ChatView`, por la misma
 * razón por la que ahí vive `useBinder`: la suscripción a Pusher es del
 * layout, no de la vista, y así el detector de caídas no se reinicia cada vez
 * que el asesor navega dentro del dashboard.
 *
 * La recarga se dispara **sólo si en ese momento la ruta es la del chat**,
 * mismo criterio que ya usa `handleNotification` en `DashboardLayout` para
 * decidir si despacha un evento entrante. El aviso visual ("Conexión
 * perdida" / "Reconectado") sí es global, porque el tiempo real lo es.
 *
 * @param {object} [options]
 * @param {object} [options.pusherClient] - Instancia de Pusher. Sólo se pasa
 *   en tests; en producción se usa el singleton de `utils/pusher`.
 */
export function useChatRealtimeRecovery({ pusherClient = pusher } = {}) {
  const dispatch = useDispatch();
  const history = useHistory();
  // `useStore` y no `useSelector` a propósito: leer la conversación abierta con
  // `useSelector` suscribiría el layout a cada cambio de `current`, cambiaría la
  // identidad del callback en cada selección y haría que `usePusherReconnect`
  // reenganchara su listener. Es la misma clase de churn que se acaba de quitar
  // de `useBinder`. Aquí sólo hace falta el valor EN EL MOMENTO de reconectar,
  // así que se lee del store sin suscribirse.
  const store = useStore();
  // A ref, not state: the flag is read synchronously by the next reconnect and
  // must not change the identity of the callback.
  const resyncInFlight = useRef(false);

  const handleReconnect = useCallback(() => {
    const pathname = history && history.location && history.location.pathname;
    if (!isChatPath(pathname)) return;

    const state = store.getState();

    // Una recarga ya en vuelo se respeta: no se encola otra encima.
    //
    // El coste de cada recarga no es una consulta de más, es la pantalla
    // entera: mientras `conversations.fetching` está en `true`, `ChatView`
    // sustituye TODO el área de chat por el loader
    // (`views/dashboard/ChatView.jsx:72-74`). Y `usePusherReconnect` no tiene
    // throttle: dispara en cada transición a `connected`. Con una red que
    // rebota —que es exactamente la que provoca las reconexiones— el asesor
    // vería el spinner a pantalla completa en bucle, con N consultas en
    // paralelo compitiendo por escribir la misma bandeja.
    //
    // Saltarse la recarga aquí no pierde nada: la que está en vuelo ya va a
    // traer el estado fresco, incluida la conversación abierta. Y el guard no
    // se puede quedar trabado: `fetching` vuelve a `false` tanto en
    // `GET_CONVERSATIONS_SUCCESS` como en `GET_CONVERSATIONS_ERROR`
    // (`conversationsDuck.js:140-149`), y el thunk envuelve todo en try/catch.
    if (isFetchingConversations(state)) return;

    const chatId = openConversationId(state) || chatIdFromPath(pathname);

    // La diferencia entre las dos ramas NO es cosmética:
    //   - con id, el thunk reselecciona la conversación y recarga sus mensajes
    //     al terminar (`conversationsDuck.js:651-653`);
    //   - sin id, el `restart` deja `current: {}`, vacía los mensajes y cierra
    //     el panel del cliente — o sea, al asesor se le CIERRA el chat abierto.
    // Por eso el id se lee de Redux y no sólo de la URL: la app nunca pone el
    // id en la URL, así que antes se caía siempre en la rama destructiva.
    const reload = () =>
      dispatch(
        chatId
          ? getConversationsAction(true, CHAT_RECONNECT_PAGE_SIZE, chatId)
          : getConversationsAction(true, CHAT_RECONNECT_PAGE_SIZE),
      );

    // Modo cola (R7): `conversationSeated`/`Unseated` events lost during the
    // outage are recovered by asking `/fill` for the seat set BEFORE reloading
    // the inbox (the backend restricts the list to those seats). With the
    // feature off the reload is exactly the one above, synchronously.
    //
    // `/fill` can take seconds, and the `fetching` guard above was evaluated
    // BEFORE that wait. So a reconnect that lands while a resync is pending is
    // dropped (the pending one will reload), and `fetching` is re-read once
    // the resync answers: an inbox load that started meanwhile is respected.
    if (state?.conversationLimit?.enabled === true) {
      if (resyncInFlight.current) return;
      resyncInFlight.current = true;
      Promise.resolve(dispatch(resyncSeatsAction()))
        .then(() => {
          resyncInFlight.current = false;
          if (!isFetchingConversations(store.getState())) reload();
        })
        .catch((error) => {
          resyncInFlight.current = false;
          Sentry.captureException(error, { tags: { component: 'useChatRealtimeRecovery', op: 'resyncSeats' } });
        });
      return;
    }
    reload();
  }, [dispatch, history, store]);

  usePusherReconnect({ pusher: pusherClient, onReconnect: handleReconnect });
}

export default useChatRealtimeRecovery;
